import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { consultingHero, caseStudies, workPhases, ctaSection } from "@/data/consultingData";
import { useLanguage } from "@/context/LanguageContext";
import { TranslationKey } from "@/translations/translations";
import { useState, useEffect } from "react";
import kevin5 from "@/assets/kevin_5.jpg";
import kevin6 from "@/assets/kevin_6.jpg";
import kevin9 from "@/assets/kevin_9.jpg";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";

const Consulting = () => {
    const { t, language } = useLanguage();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const images = [kevin5, kevin6, kevin9];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [images.length]);

    return (
        <main id="main-content" className="min-h-screen w-full overflow-x-hidden">
            <SEO
                title={t("seo.consulting.title")}
                description={t("seo.consulting.description")}
                keywords={t("seo.consulting.keywords").split(",").map((k) => k.trim())}
            />
            <div className="container mx-auto px-2 sm:px-4 py-12">
                <Breadcrumbs />


                <div className="max-w-5xl mx-auto">
                    {/* Hero Section */}
                    <header className="mb-16 animate-fade-in">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            {t("caseStudies.hero.title")}
                        </h1>
                        <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                            {t("caseStudies.hero.subtitle")}
                        </p>
                    </header>

                    {/* Case Studies Section */}
                    <section className="mb-16" aria-label="Case Studies">
                        <h2 className="text-3xl md:text-4xl font-bold mb-8 animate-fade-in">
                            {t("caseStudies.heading")}
                        </h2>

                        <div className="space-y-8">
                            {caseStudies.map((study, index) => (
                                <Card
                                    key={study.company}
                                    className="border-primary/20 bg-card/50 backdrop-blur-sm animate-scale-in"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <CardHeader>
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <CardTitle className="text-2xl md:text-3xl mb-2">
                                                    {t(study.company)}: {t(study.title)}
                                                </CardTitle>
                                                {study.timeline && (
                                                    <p className="text-sm text-muted-foreground font-medium">
                                                        {t(study.timeline)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {/* Challenge */}
                                        <div>
                                            <h3 className="text-lg font-semibold mb-2 text-primary">
                                                {t("caseStudies.challenge")}
                                            </h3>
                                            <p className="text-muted-foreground leading-relaxed">
                                                {t(study.challenge)}
                                            </p>
                                        </div>

                                        {/* What I Did */}
                                        <div>
                                            <h3 className="text-lg font-semibold mb-2 text-primary">
                                                {t("caseStudies.whatIDid")}
                                            </h3>
                                            <p className="text-muted-foreground leading-relaxed">
                                                {t(study.whatIDid).split(/(\[.*?\]\(.*?\))/g).map((part, i) => {
                                                    const match = part.match(/\[(.*?)\]\((.*?)\)/);
                                                    if (match) {
                                                        return (
                                                            <a
                                                                key={i}
                                                                href={match[2]}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary hover:underline hover:text-primary/80 transition-colors"
                                                            >
                                                                {match[1]}
                                                                <span className="sr-only">
                                                                    {language === 'de' ? ' (öffnet in neuem Tab)' : ' (opens in a new tab)'}
                                                                </span>
                                                            </a>
                                                        );
                                                    }
                                                    return part;
                                                })}
                                            </p>
                                        </div>

                                        {/* Impact */}
                                        <div>
                                            <h3 className="text-lg font-semibold mb-2 text-primary">
                                                {t("caseStudies.impact")}
                                            </h3>
                                            <ul className="space-y-3">
                                                {study.impact.map((item, i) => (
                                                    <li key={i} className="flex items-start">
                                                        <span className="text-primary mr-2 mt-1">•</span>
                                                        <span className="text-muted-foreground leading-relaxed">
                                                            {t(item)}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Systemic Intervention (if exists) */}
                                        {study.systemicIntervention && (
                                            <div className="bg-primary/5 p-4 rounded-lg border border-primary/10">
                                                <h3 className="text-lg font-semibold mb-2 text-primary">
                                                    {t("caseStudies.systemicIntervention")}
                                                </h3>
                                                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                                                    {t(study.systemicIntervention)}
                                                </p>
                                            </div>
                                        )}

                                        {/* What This Demonstrates (if exists) */}
                                        {study.whatThisDemonstrates && (
                                            <div className="bg-accent/5 p-4 rounded-lg border border-accent/10">
                                                <h3 className="text-lg font-semibold mb-2 text-accent">
                                                    {t("caseStudies.whatThisDemonstrates")}
                                                </h3>
                                                <p className="text-muted-foreground leading-relaxed">
                                                    {t(study.whatThisDemonstrates)}
                                                </p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>
                </div>
            </div>

            {/* Full Width Image Gallery Section */}
            <div
                className="w-full h-[80vh] relative mb-16 overflow-hidden"
                style={{
                    clipPath: "polygon(0 10vw, 100% 0, 100% calc(100% - 10vw), 0 100%)"
                }}
            >
                {/* Desktop View */}
                <div className="hidden md:flex w-full h-full">
                    {images.map((img, index) => (
                        <div key={index} className="w-1/3 h-full relative border-r border-white/10 last:border-r-0 overflow-hidden group">
                            <img
                                src={img}
                                alt={t(`consulting.gallery.alt${index + 1}` as TranslationKey)}
                                title={t(`consulting.gallery.title${index + 1}` as TranslationKey)}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                loading="lazy"
                            />
                        </div>
                    ))}
                </div>

                {/* Mobile View - Slideshow */}
                <div className="md:hidden w-full h-full relative">
                    {images.map((img, index) => (
                        <div
                            key={index}
                            className={`absolute inset-0 transition-opacity duration-1000 ${currentImageIndex === index ? 'opacity-100' : 'opacity-0'
                                }`}
                        >
                            <img
                                src={img}
                                alt={t(`consulting.gallery.alt${index + 1}` as TranslationKey)}
                                title={t(`consulting.gallery.title${index + 1}` as TranslationKey)}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className="container mx-auto px-2 sm:px-4">
                <div className="max-w-5xl mx-auto">
                    {/* How I Work Section */}
                    <section className="mb-16" aria-label="How I Work">
                        <h2 className="text-3xl md:text-4xl font-bold mb-8 animate-fade-in">
                            {t("caseStudies.howIWork")}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold">{t("consulting.processModel")}</h3>
                                <div className="rounded-xl overflow-hidden border border-white/10 bg-black/20 p-4">
                                    <img
                                        src="/images/role_transfer_diagram_dark_v2.png"
                                        alt={t("consulting.diagram.process.alt" as TranslationKey)}
                                        title={t("consulting.diagram.process.alt" as TranslationKey)}
                                        className="w-full h-auto object-contain hover:scale-105 transition-transform duration-500"
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                            <div className="space-y-4">
                                <h3 className="text-xl font-semibold">{t("consulting.competenceModel")}</h3>
                                <div className="rounded-xl overflow-hidden border border-white/10 bg-black/20 p-4">
                                    <img
                                        src="/images/competence_model.png"
                                        alt={t("consulting.diagram.competence.alt" as TranslationKey)}
                                        title={t("consulting.diagram.competence.alt" as TranslationKey)}
                                        className="w-full h-auto object-contain hover:scale-105 transition-transform duration-500"
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {workPhases.map((phase, index) => (
                                <Card
                                    key={phase.number}
                                    className="border-primary/20 bg-card/50 backdrop-blur-sm animate-scale-in"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <CardHeader>
                                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                                            <span className="text-2xl font-bold text-primary">{phase.number}</span>
                                        </div>
                                        <CardTitle className="text-xl">{t(phase.title)}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <CardDescription className="text-base leading-relaxed">
                                            {t(phase.description)}
                                        </CardDescription>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </section>

                    {/* CTA Section */}
                    <div className="text-center py-12 animate-fade-in">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            {t("caseStudies.cta.title")}
                        </h2>
                        <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                            {t("caseStudies.cta.description")}
                        </p>
                        <Button
                            variant="hero"
                            size="lg"
                            onClick={() => window.location.href = 'mailto:kevin.rassner@gmail.com'}
                            className="group"
                        >
                            {t("caseStudies.cta.button")}
                            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </div>
                </div>
            </div>
            <Footer />
        </main>
    );
};

export default Consulting;
